import React, { useId, useMemo, useState } from 'react';
import { cn } from './cn';

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (ts instanceof Date) return ts;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (d) => `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
const fmtDay = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
export const fmtEuroShort = (v) => {
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (v >= 1_000) return `€${Math.round(v / 1_000)}k`;
  return `€${Math.round(v)}`;
};
const fmtEuro = (v) => `€${Math.round(v).toLocaleString()}`;

/**
 * Normalise a Firestore-style history array into sorted {date, price} points.
 * Entries without a valid date or a positive numeric price are dropped.
 */
export const normalizeHistory = (history = []) =>
  (history || [])
    .map((h) => ({ date: toDate(h.snapshotTimestamp), price: Number(h.price) }))
    .filter((p) => p.date && Number.isFinite(p.price) && p.price > 0)
    .sort((a, b) => a.date - b.date);

/**
 * Dependency-free SVG price history chart.
 * Renders nothing with fewer than 2 valid points.
 */
const PriceHistoryChart = ({ history, className, height = 180 }) => {
  const points = useMemo(() => normalizeHistory(history), [history]);
  const gradId = useId();
  const glowId = useId();
  const [hover, setHover] = useState(null);

  if (points.length < 2) return null;

  const W = 600;
  const H = height;
  const padL = 46;
  const padR = 16;
  const padT = 18;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const minP = Math.min(...points.map((p) => p.price));
  const maxP = Math.max(...points.map((p) => p.price));
  const spread = maxP - minP || maxP * 0.1 || 1;
  const yMin = Math.max(0, minP - spread * 0.25);
  const yMax = maxP + spread * 0.25;
  const t0 = points[0].date.getTime();
  const t1 = points[points.length - 1].date.getTime();
  const tSpan = t1 - t0 || 1;

  const x = (d) => padL + ((d.getTime() - t0) / tSpan) * innerW;
  const y = (v) => padT + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const coords = points.map((p) => ({ ...p, cx: x(p.date), cy: y(p.price) }));

  // Step-after path: price stays flat until the next snapshot, which is how listing prices behave.
  const linePath = coords
    .map((c, i) => {
      if (i === 0) return `M ${c.cx.toFixed(1)} ${c.cy.toFixed(1)}`;
      const prev = coords[i - 1];
      return `L ${c.cx.toFixed(1)} ${prev.cy.toFixed(1)} L ${c.cx.toFixed(1)} ${c.cy.toFixed(1)}`;
    })
    .join(' ');
  const areaPath = `${linePath} L ${coords[coords.length - 1].cx.toFixed(1)} ${(padT + innerH).toFixed(1)} L ${coords[0].cx.toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;

  // Y ticks: 3 evenly spaced.
  const yTicks = [yMin, (yMin + yMax) / 2, yMax];

  // X ticks: up to 5 evenly spaced dates.
  const xTickCount = Math.min(5, Math.max(2, points.length));
  const xTicks = Array.from({ length: xTickCount }, (_, i) => new Date(t0 + (tSpan * i) / (xTickCount - 1)));

  const last = coords[coords.length - 1];
  const active = hover != null ? coords[hover] : last;
  const tipW = 150;
  const tipX = Math.min(Math.max(active.cx - tipW / 2, padL), W - padR - tipW);
  const tipY = Math.max(2, active.cy - 40);

  return (
    <div className={cn('w-full', className)}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Price history from ${fmtEuro(points[0].price)} to ${fmtEuro(last.price)}`}
        className="w-full h-auto select-none"
        data-testid="price-history-chart"
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1" data-testid="price-history-gradient">
            <stop offset="0%" stopColor="#14D3B0" stopOpacity="0.55" />
            <stop offset="60%" stopColor="#14D3B0" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#14D3B0" stopOpacity="0" />
          </linearGradient>
          <filter id={glowId} x="-20%" y="-50%" width="140%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Grid */}
        {yTicks.map((v, i) => (
          <g key={`y${i}`}>
            <line
              x1={padL}
              x2={W - padR}
              y1={y(v)}
              y2={y(v)}
              stroke="#3A4A66"
              strokeDasharray="3 5"
              strokeWidth="1"
            />
            <text x={padL - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#A3B1C9" className="tabular">
              {fmtEuroShort(v)}
            </text>
          </g>
        ))}
        {xTicks.map((d, i) => (
          <g key={`x${i}`}>
            <line x1={x(d)} x2={x(d)} y1={padT} y2={padT + innerH} stroke="#3A4A66" strokeDasharray="3 5" strokeWidth="1" />
            <text
              x={x(d)}
              y={H - 8}
              textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
              fontSize="11"
              fill="#A3B1C9"
            >
              {fmtMonth(d)}
            </text>
          </g>
        ))}

        {/* Area + line */}
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath} fill="none" stroke="#2EF2C6" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" filter={`url(#${glowId})`} />

        {/* Markers */}
        {coords.map((c, i) => (
          <g key={i} data-testid="price-history-marker">
            <circle cx={c.cx} cy={c.cy} r="12" fill="transparent" onMouseEnter={() => setHover(i)} onTouchStart={() => setHover(i)} />
            <circle
              cx={c.cx}
              cy={c.cy}
              r={i === coords.length - 1 || hover === i ? 5.5 : 4.5}
              fill={i === coords.length - 1 ? '#2EF2C6' : '#FFB020'}
              stroke="#1E2839"
              strokeWidth="2"
              className="pointer-events-none"
            />
          </g>
        ))}

        {/* Tooltip */}
        <g className="pointer-events-none">
          <line x1={active.cx} x2={active.cx} y1={active.cy} y2={tipY + 30} stroke="#2EF2C6" strokeWidth="1" strokeDasharray="2 3" />
          <rect x={tipX} y={tipY} width={tipW} height="28" rx="7" fill="#14D3B0" />
          <text x={tipX + tipW / 2} y={tipY + 18} textAnchor="middle" fontSize="12" fontWeight="600" fill="#062A24">
            {fmtEuro(active.price)} · {fmtDay(active.date)}
          </text>
        </g>
      </svg>
    </div>
  );
};

export default PriceHistoryChart;
